/**
 * Specifications: SDDs and PRDs, and what is happening against each — the report view.
 *
 * Three things a manager asks about a project, answered from what agents reported:
 *
 * 1. **How much of it is delivered?** The agents' own estimate, compared against the SDDs, with the
 *    model and the date. DevPulse asks for it about once a day on its own; a manager can ask for a
 *    fresh one. It is labelled as an estimate everywhere, because that is what it is.
 * 2. **What is each specification getting?** Who is working on it now, its tasks, the bugs raised
 *    against it, the documents written for it.
 * 3. **How far are the documents?** The phase view that was here before, kept below.
 */

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bug as BugIcon, FileText, Info, RefreshCw, Sparkles, Users } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { formatDuration, formatRelative } from '../lib/format'
import { Badge, EmptyState, Panel, ProgressBar, Skeleton } from '../components/primitives'
import { Portrait, lookFor } from '../bughunt/Figures'
import SddPhases from './Sdd'
import { LinkedDocs } from '../components/LinkedDocs'
import type { ProjectEstimate, SpecSummary } from '../lib/types'
import { specProgress } from '../lib/specProgress'

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warn' | 'ok'> = {
  Draft: 'neutral',
  InProgress: 'accent',
  InReview: 'warn',
  Done: 'ok',
}

export default function Specs() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const selected = params.get('spec')
  const [kind, setKind] = useState('')
  const [projectId, setProjectId] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['specs', { kind, projectId }],
    queryFn: () => api.specs.list({ kind: kind || undefined, projectId: projectId || undefined }),
    refetchInterval: 15_000,
  })

  const select = (id: string | null): void => {
    const next = new URLSearchParams(params)
    if (id) next.set('spec', id)
    else next.delete('spec')
    setParams(next, { replace: true })
  }

  return (
    <div className="stack stack--loose">
      <div className="row row--wrap">
        <div className="segmented">
          {[
            { value: '', label: t('common.all') },
            { value: 'Prd', label: 'PRD' },
            { value: 'Sdd', label: 'SDD' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              className={`segmented__item${kind === option.value ? ' segmented__item--active' : ''}`}
              onClick={() => setKind(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {(data?.projects.length ?? 0) > 0 && (
          <select className="select" style={{ maxWidth: 220 }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">{t('specs.allProjects')}</option>
            {data!.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
        )}

        <div className="spacer" />

        {data && (
          <span className="tiny muted">
            {t('specs.summary', { total: data.counts.total, working: data.counts.peopleWorking, bugs: data.counts.openBugs })}
          </span>
        )}
      </div>

      {(data?.estimates.length ?? 0) > 0 && (
        <div className="grid grid--two">
          {data!.estimates.map((estimate) => <EstimateCard key={estimate.projectId} estimate={estimate} />)}
        </div>
      )}

      {isLoading ? (
        <Skeleton height={220} />
      ) : !data || data.specs.length === 0 ? (
        <Panel>
          <EmptyState title={t('specs.empty')} body={t('specs.emptyHint')} />
        </Panel>
      ) : (
        <div className="specs">
          <Panel flush>
            <div className="specs__list">
              {data.specs.map((spec) => (
                <SpecRow key={spec.id} spec={spec} active={spec.id === selected} onClick={() => select(spec.id === selected ? null : spec.id)} />
              ))}
            </div>
          </Panel>

          {selected && <SpecDetailPanel id={selected} onClose={() => select(null)} />}
        </div>
      )}

      <details className="specs__phases">
        <summary className="bold">{t('specs.phases')}</summary>
        <div style={{ marginTop: 'var(--s4)' }}>
          <SddPhases />
        </div>
      </details>
    </div>
  )
}

/**
 * The latest estimate of how much of a project is delivered.
 *
 * Always shown with who made it, with what, and when. "Not estimated yet" is its own state rather
 * than a zero: a project nobody has estimated is not a project with nothing delivered.
 */
function EstimateCard({ estimate }: { estimate: ProjectEstimate }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const canAsk = atLeast(getSession()?.role, 'EngineeringManager')

  const ask = useMutation({
    mutationFn: () => api.specs.requestEstimate(estimate.projectId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['specs'] }),
  })

  return (
    <Panel
      title={estimate.projectName}
      subtitle={t('specs.estimateTitle')}
      actions={
        canAsk && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => ask.mutate()}
            disabled={ask.isPending || estimate.requested}
            title={t('specs.askHint')}
          >
            <RefreshCw size={13} /> {estimate.requested ? t('specs.asked') : t('specs.ask')}
          </button>
        )
      }
    >
      {estimate.percent == null ? (
        <span className="small muted">{t('specs.notEstimated')}</span>
      ) : (
        <div className="stack stack--tight">
          <div className="row row--tight">
            <Sparkles size={15} className="muted" />
            <span className="specs__estimate">{Math.round(estimate.percent)}%</span>
            <span className="tiny muted">{t('specs.delivered')}</span>
          </div>
          <ProgressBar value={estimate.percent} size="sm" />
          {estimate.rationale && <span className="small">{estimate.rationale}</span>}
          <span className="tiny muted">
            {t('specs.estimatedBy', {
              model: estimate.modelName ?? '—',
              who: estimate.developerName ?? '—',
              when: estimate.estimatedAt ? formatRelative(estimate.estimatedAt, locale) : '—',
            })}
          </span>
        </div>
      )}
      {estimate.requested && <span className="tiny muted">{t('specs.askedHint')}</span>}
    </Panel>
  )
}

function SpecRow({ spec, active, onClick }: { spec: SpecSummary; active: boolean; onClick: () => void }) {
  const { t, locale } = useI18n()
  const working = spec.liveSessions.filter((s) => s.activityState !== 'Idle')
  const progress = specProgress(spec)

  return (
    <button type="button" className={`specs__row${active ? ' specs__row--active' : ''}`} onClick={onClick}>
      <div className="row row--tight">
        <Badge tone={spec.kind === 'Prd' ? 'purple' : 'info'}>{spec.kind === 'Prd' ? 'PRD' : 'SDD'}</Badge>
        <span className="mono small bold">{spec.key}</span>
        <span className="small truncate">{spec.title}</span>
        <div className="spacer" />
        {spec.isAgentDiscovered && <Badge tone="warn">{t('specs.discovered')}</Badge>}
        <Badge tone={STATUS_TONE[spec.status] ?? 'neutral'}>{t(`specs.status.${spec.status}`)}</Badge>
      </div>

      {/*
        How much of it is done, where the eye lands rather than among the footnotes — and always
        saying which of the three things it measures. "Not estimated" is its own state: a
        specification nobody has looked at is not one with nothing delivered.
      */}
      <div className={`specs__progress specs__progress--${progress?.kind ?? 'none'}`}>
        {progress ? (
          <>
            <span className="specs__progress-figure">
              {progress.isEstimate ? '~' : ''}{Math.round(progress.percent)}%
            </span>
            <ProgressBar value={progress.percent} size="sm" />
            <span className="tiny muted nowrap">{t(`specs.measure.${progress.kind}`)}</span>
          </>
        ) : (
          <span className="tiny muted">{t('specs.measure.none')}</span>
        )}
      </div>

      <div className="row row--wrap tiny muted">
        {spec.projectName && <span>{spec.projectName}</span>}
        {spec.taskCount > 0 && <span>{t('specs.tasksDone', { done: spec.tasksDone, total: spec.taskCount })}</span>}
        {spec.openBugCount > 0 && (
          <span style={{ color: 'var(--danger-text)' }}>
            <BugIcon size={11} /> {t('specs.openBugs', { count: spec.openBugCount })}
          </span>
        )}
        {spec.documentCompleteness != null && progress?.kind !== 'document' && (
          <span>{t('specs.documentShort', { percent: Math.round(spec.documentCompleteness) })}</span>
        )}
        {spec.lastActivityAt && <span>{formatRelative(spec.lastActivityAt, locale)}</span>}
        <div className="spacer" />
        {working.length > 0 && (
          <span className="row row--tight">
            <Users size={11} />
            {working.slice(0, 4).map((session) => (
              <Portrait key={session.workSessionId} look={lookFor(session.developerId, session.avatar)} size={20} />
            ))}
          </span>
        )}
      </div>
    </button>
  )
}

function SpecDetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, locale } = useI18n()
  const canLinkDocs = atLeast(getSession()?.role, 'Developer')
  const { data, isLoading } = useQuery({
    queryKey: ['specs', 'detail', id],
    queryFn: () => api.specs.detail(id),
    refetchInterval: 15_000,
  })

  const bugs = useMemo(() => (data?.tasks ?? []).filter((task) => task.type === 'Bug'), [data])
  const tasks = useMemo(() => (data?.tasks ?? []).filter((task) => task.type !== 'Bug'), [data])

  if (isLoading || !data) return <Skeleton height={260} />

  const spec = data.spec

  return (
    <Panel
      title={`${spec.key} — ${spec.title}`}
      subtitle={[spec.kind === 'Prd' ? 'PRD' : 'SDD', spec.projectName, spec.documentPath].filter(Boolean).join(' · ')}
      actions={<button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>{t('common.close')}</button>}
    >
      <div className="stack">
        {data.description && <p className="small">{data.description}</p>}

        {spec.liveSessions.length > 0 && (
          <div className="stack stack--tight">
            <span className="field__label">{t('specs.workingNow')}</span>
            {spec.liveSessions.map((session) => (
              <Link key={session.workSessionId} to={`/work/${session.workSessionId}`} className="specs__session">
                <Portrait look={lookFor(session.developerId, session.avatar)} size={28} />
                <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
                  <span className="small bold">{session.developerName}</span>
                  <span className="tiny muted truncate">{session.currentNote ?? session.currentStepTitle ?? session.taskExternalId ?? ''}</span>
                </div>
                <span className="tiny">{Math.round(session.progressPercent)}%</span>
              </Link>
            ))}
          </div>
        )}

        {bugs.length > 0 && (
          <div className="stack stack--tight">
            <span className="field__label">{t('specs.bugs')}</span>
            {bugs.map((bug) => (
              <div key={bug.id} className="row row--tight small">
                <BugIcon size={13} className="muted" />
                <span className="mono">{bug.externalId}</span>
                <span className="truncate">{bug.title}</span>
                <div className="spacer" />
                <Badge tone={bug.status === 'Done' ? 'ok' : 'danger'}>{t(`taskStatus.${bug.status}`)}</Badge>
              </div>
            ))}
          </div>
        )}

        {tasks.length > 0 && (
          <div className="stack stack--tight">
            <span className="field__label">{t('specs.tasks')}</span>
            {tasks.map((task) => (
              <div key={task.id} className="row row--tight small">
                <span className="mono">{task.externalId}</span>
                <span className="truncate">{task.title}</span>
                <div className="spacer" />
                <span className="tiny muted">{task.assignedDeveloperName ?? ''}</span>
                <Badge tone={task.status === 'Done' ? 'ok' : 'neutral'}>{t(`taskStatus.${task.status}`)}</Badge>
              </div>
            ))}
          </div>
        )}

        {data.documents.length > 0 && (
          <div className="stack stack--tight">
            <span className="field__label">{t('specs.documents')}</span>
            {data.documents.map((document) => (
              <div key={document.documentPath} className="row row--tight tiny">
                <FileText size={12} className="muted" />
                {/* A path, never contents. */}
                <span className="mono truncate">{document.documentPath}</span>
                <div className="spacer" />
                {document.completenessPercent != null && <span>{Math.round(document.completenessPercent)}%</span>}
                <span className="muted">{formatRelative(document.lastUpdatedAt, locale)}</span>
              </div>
            ))}
          </div>
        )}

        {data.recentSessions.length > 0 && (
          <div className="stack stack--tight">
            <span className="field__label">{t('specs.recent')}</span>
            {data.recentSessions.map((session) => (
              <Link key={session.workSessionId} to={`/work/${session.workSessionId}`} className="row row--tight tiny">
                <span className="bold">{session.developerName}</span>
                <span className="truncate muted">{session.subject ?? ''}</span>
                <div className="spacer" />
                <span>+{session.linesAdded} −{session.linesDeleted}</span>
                <span className="muted">{formatDuration(session.durationSeconds, locale)}</span>
                <span className="muted">{formatRelative(session.startedAt, locale)}</span>
              </Link>
            ))}
          </div>
        )}

        <LinkedDocs specificationId={spec.id} canEdit={canLinkDocs} />

        <div className="row row--tight tiny muted">
          <Info size={12} />
          <span>{t('specs.claimNote')}</span>
        </div>
      </div>
    </Panel>
  )
}
